import React, { useEffect, useState } from "react";
import { useRouter } from "next/router";
import { Button, Label, Card, Icons, Text, Image } from "../../design";
import {
    translate,
    translateIntervals,
    translateDateEpoch,
} from "../../utils/translations";
import {
    SUBSCRIPTION_STATUS_READABLE,
    DB_SUBSCRIPTION_STATUS_ACTIVE,
    DB_SUBSCRIPTION_STATUS_CANCELLED,
    DB_SUBSCRIPTION_STATUS_PAUSED,
    DB_SUBSCRIPTION_STATUS_EXPIRED,
} from "../../utils/constants";
import {
    getCurrencyDisplayInternationalized,
    getVariantTitleDisplay,
} from "../../utils/helpers";
import { useLoopAuth } from "../auth-provider";
import { addDays, fromUnixTime } from "date-fns";
import { useLoop } from "../loop-provider";
import useNavigationURL from "../../hooks/useNavigationURL";

const getSubscriptionLabel = (contract: any) => {
    if (contract.payment_method?.risk_level == "expired") {
        return translate("subscription_listing_create_payment_failed_label");
    }
    if (contract.last_billing_attempt?.error_code != null) {
        if (contract.payment_method.update_mail_sent_at == null) {
            return translate(
                "subscription_listing_create_last_billing_failed_label",
            );
        }

        const _updatedAt = new Date(contract.payment_method.paymentMethodLastUpdatedAt);
        const _baCreatedAt = new Date(contract.last_billing_attempt.createdAt);

        if (_baCreatedAt >= _updatedAt) {
            return translate(
                "subscription_listing_create_last_billing_failed_label",
            );
        }
    }
    return null;
};

function SubscriptionCard({ contract, customer_shopify_id, storeDefaultLocale }) {
    const router = useRouter();
    const { queryString } = useLoopAuth();
    const { locale, country, preferences } = useLoop();
    const navigationUrl = useNavigationURL();

    const [cancelledExpiredOrPaused, setcancelledExpiredOrPaused] = useState(true);

    useEffect(() => {
        let _cancelledPausedOrExpired = false;
        if (
            contract.status == DB_SUBSCRIPTION_STATUS_CANCELLED ||
            contract.status == DB_SUBSCRIPTION_STATUS_PAUSED ||
            contract.status == DB_SUBSCRIPTION_STATUS_EXPIRED
        ) {
            _cancelledPausedOrExpired = true;
        }
        setcancelledExpiredOrPaused(_cancelledPausedOrExpired);
    }, [contract]);

    const frequencyText = (line_items: any) => {
        if (contract.isPrepaidV2) {
            // Check if the auto-renewal for subscription is enabled
            if (contract.billing_policy.max_cycles != null) {
                return translate("subscription_plan_nonprepaid_label", {
                    delivery_interval: translateIntervals(contract.delivery_policy.interval_count, contract.delivery_policy.interval.toLowerCase()),
                });
            }

            return translate("subscription_plan_prepaid_label", {
                delivery_interval: translateIntervals(contract.delivery_policy.interval_count, contract.delivery_policy.interval.toLowerCase()),
                billing_interval: translateIntervals(contract.billing_policy.prepaid_interval_count, contract.billing_policy.interval.toLowerCase()),
            });
        }

        // Selling plan name
        if (line_items && line_items.length >= 1 && (locale === storeDefaultLocale)) {
            const sellingPlan = new Set();
            line_items.forEach((item: any) => {
                sellingPlan.add(item.selling_plan_id);
            });
            if (sellingPlan.size == 1) {
                const isSameBillingInterval = line_items?.[0]?.sellingPlanBillingInterval === contract?.billing_policy?.interval && line_items?.[0]?.sellingPlanBillingIntervalCount === contract?.billing_policy?.interval_count;
                const isSameDeliveryInterval = line_items?.[0]?.sellingPlanDeliveryInterval === contract?.delivery_policy?.interval && line_items?.[0]?.sellingPlanDeliveryIntervalCount === contract?.delivery_policy?.interval_count;
                if (line_items?.[0]?.selling_plan_name && isSameBillingInterval && isSameDeliveryInterval) {
                    return line_items[0].selling_plan_name;
                }
            }
        }


        if (contract.delivery_policy.interval_count == contract.billing_policy.interval_count) {
            // Non-prepaid
            return translate("subscription_plan_nonprepaid_label", {
                delivery_interval: translateIntervals(contract.delivery_policy.interval_count, contract.delivery_policy.interval.toLowerCase()),
            });
        }

        // Prepaid-V1
        return translate("subscription_plan_prepaid_label", {
            delivery_interval: translateIntervals(contract.delivery_policy.interval_count, contract.delivery_policy.interval.toLowerCase()),
            billing_interval: translateIntervals(contract.billing_policy.interval_count, contract.billing_policy.interval.toLowerCase()),
        });
    }

    const getNextOrderDate = () => {
        if (contract.chargeOffset) {
            const _deliveryDate = addDays(
                fromUnixTime(contract.next_billing_date),
                contract.chargeOffset,
            );
            return translateDateEpoch(
                Math.ceil(_deliveryDate.getTime() / 1000),
                preferences.date_display_internationalization_format,
                locale,
                country,
            );
        } else {
            return translateDateEpoch(contract.next_billing_date, preferences.date_display_internationalization_format, locale, country);
        }
    };
    return (
        <Card
            color="dark"
            className="my-4"
            onClick={() => {
                router.push(
                    `${navigationUrl}/customer/${customer_shopify_id}/subscription/${contract.shopify_id}${queryString.length ? `?${queryString}` : ""}`,
                );
            }}
            label={
                !cancelledExpiredOrPaused
                    ? getSubscriptionLabel(contract)
                    : null
            }
        >
            <Card.Header>
                <div className="d-flex justify-content-between align-items-center mb-1">
                    <Text.P1 className="loop-text-muted">
                        {"#" + contract.shopify_id}
                    </Text.P1>
                    <Label
                        entity="subscription"
                        dbStatus={contract.status}
                        title={translate(
                            "subscription_status_" +
                            SUBSCRIPTION_STATUS_READABLE[contract.status],
                        )}
                    />
                </div>
                {contract.status == DB_SUBSCRIPTION_STATUS_ACTIVE ||
                    (contract.status == DB_SUBSCRIPTION_STATUS_PAUSED &&
                        contract.autoResume) ? (
                    <div className="d-flex justify-content-between align-items-center mb-1">
                        <Text.P1>
                            {translate("next_order_date_label", {
                                next_billing_date: getNextOrderDate(),
                            }) + " "}
                            <strong className="loop-contract-card-nbd">
                                {getNextOrderDate()}
                            </strong>
                        </Text.P1>
                    </div>
                ) : null}
                <div className="d-flex align-items-center">
                    <Icons.Repeat />
                    <Text.P2 className="ms-1">
                        {frequencyText(contract.line_items)}
                    </Text.P2>
                </div>
            </Card.Header>
            <div className="row p-2">
                {contract.line_items.map((li: any, liIndex: number) => {
                    let _break = null;
                    if (liIndex > 0 && liIndex % 2 == 0) {
                        _break = <div className="w-100 d-block"></div>;
                    }
                    return (
                        <React.Fragment key={liIndex}>
                            {_break}
                            <div className="col-md" id={li.id}>
                                <div className="d-flex align-items-center p-2">
                                    <Image.ProductImage
                                        size="sm"
                                        className="me-3 flex-shrink-0"
                                        src={li.variant_image}
                                    />
                                    <div>
                                        <Text.P1>{li.title}</Text.P1>
                                        <Text.P2>
                                            {getVariantTitleDisplay(
                                                li.variant_title,
                                            )}
                                        </Text.P2>
                                        <Text.P2>
                                            {getCurrencyDisplayInternationalized(contract.currency_code, locale, country, li.discounted_price / li.quantity)}
                                            {" x "}
                                            {li.quantity}
                                        </Text.P2>
                                        {li.is_one_time_added == 1 ? (
                                            <Label
                                                variant="light"
                                                className="mb-2"
                                                size="sm"
                                                title={translate(
                                                    "product_card_one_time_added_label",
                                                )}
                                            />
                                        ) : null}
                                        {li.is_one_time_removed == 1 ? (
                                            <Label
                                                variant="light"
                                                className="mb-2"
                                                size="sm"
                                                title={translate(
                                                    "product_card_one_time_removed_label",
                                                )}
                                            />
                                        ) : null}
                                    </div>
                                </div>
                            </div>
                        </React.Fragment>
                    );
                })}
            </div>
            <Card.Footer>
                <div className="d-flex align-items-center justify-content-between">
                    <Text.P1>
                        <Text.P1>
                            <strong id="loop-subscription-list-total-price">
                                {getCurrencyDisplayInternationalized(contract.currency_code, locale, country, contract.total_line_item_discounted_price)}
                            </strong>
                            <span id="loop-subscription-list-plus-separator">{" + "}</span>
                            {
                                contract?.discounts?.filter((item: any) => item?.node?.targetType === "SHIPPING_LINE")?.length ?
                                    <span id="loop-subscription-list-shipping-cost">
                                        {getCurrencyDisplayInternationalized(contract.delivery_price_currency_code, locale, country, 0.00)} {translate("shipping_label")}
                                    </span>
                                    :
                                    <span id="loop-subscription-list-shipping-cost">
                                        {getCurrencyDisplayInternationalized(contract.delivery_price_currency_code, locale, country, contract?.delivery_price ?? 0.00)} {translate("shipping_label")}
                                    </span>
                            }
                        </Text.P1>
                    </Text.P1>
                    <Button
                        size="sm"
                        id='loop-subscription-list-view-details-button'
                        onClick={() => {
                            router.push(`${navigationUrl}/customer/${customer_shopify_id}/subscription/${contract.shopify_id}${queryString.length ? `?${queryString}` : ""}`);
                        }}
                    >
                        {translate(
                            "subscription_listing_view_details_button",
                        )}
                    </Button>
                </div>
            </Card.Footer>
        </Card>
    );
}

export default SubscriptionCard;
